import storage from 'local-storage-fallback'
import { v4 as uuid } from 'uuid'
import omit from 'lodash.omit'
import omitBy from 'lodash.omitby'
import orderBy from 'lodash.orderby'
import isUndefined from 'lodash.isundefined'

// Récupération sur échec de chargement du panier (incident du 16-17/09).
const INIT_TENTATIVES = 3
const INIT_DELAI_MS = 400
const attendre = ms => new Promise(resolve => setTimeout(resolve, ms))

export default {
  state () {
    return {
      cart: null,
      show: false,
      wishlists: [],
      ready: false,
      pusherChannel: null,
      guestEmail: null,
      billingAddress: null,
      guestAddresses: []
    }
  },
  mutations: {
    setCart (state, cart) {
      state.cart = cart
    },
    setGuestEmail (state, email) {
      state.guestEmail = email
    },
    setBillingAddress (state, address) {
      state.billingAddress = address
    },
    updateGuestAddress (state, { oldAddress, newAddress }) {
      const clonedNew = JSON.parse(JSON.stringify(newAddress))
      const index = state.guestAddresses.findIndex(a =>
        a.street_address === oldAddress.street_address &&
        a.postal_code === oldAddress.postal_code &&
        a.city === oldAddress.city
      )
      if (index !== -1) {
        state.guestAddresses.splice(index, 1, clonedNew)
      }
    },
    addGuestAddress (state, address) {
      const cloned = JSON.parse(JSON.stringify(address))
      const isDuplicate = state.guestAddresses.some(a =>
        a.street_address === cloned.street_address &&
        a.postal_code === cloned.postal_code &&
        a.city === cloned.city
      )
      if (!isDuplicate) {
        state.guestAddresses.push(cloned)
      }
    },
    setShow (state, show) {
      state.show = show
    },
    setWishlists (state, wishlists) {
      state.wishlists = wishlists
    },
    setReady (state, ready) {
      state.ready = ready
    },
    setPusherChannel (state, channel) {
      state.pusherChannel = () => channel
    }
  },
  actions: {
    async init ({ commit, dispatch, state }) {
      commit('setReady', false)
      let cartId = storage.getItem('yellaw-cart-id')
      if (!cartId) {
        cartId = uuid()
        storage.setItem('yellaw-cart-id', cartId)
      }
      // Un seul échec ici et `setReady(true)` n'est jamais atteint : le panier du
      // visiteur reste mort jusqu'au rechargement, et l'init de l'app s'arrête
      // (cf. plugins/app.js). Observé en production le 17/09 sur un mobile
      // ("Network Error"). On réessaie donc avant d'abandonner.
      let cart
      let derniereErreur
      for (let tentative = 1; tentative <= INIT_TENTATIVES; tentative++) {
        try {
          ({ data: cart } = await this.$api.get(`/carts/${cartId}`))
          derniereErreur = null
          if (tentative > 1 && this.$trace) {
            this.$trace('panier_init_retabli', { tentative })
          }
          break
        } catch (error) {
          derniereErreur = error
          if (this.$trace) {
            this.$trace('panier_init_echec', {
              tentative,
              statut: (error && error.response && error.response.status) || null,
              message: (error && error.message) || String(error)
            })
          }
          if (tentative < INIT_TENTATIVES) {
            await attendre(INIT_DELAI_MS * tentative)
          }
        }
      }
      if (derniereErreur) {
        throw derniereErreur
      }
      if (state.pusherChannel) {
        state.pusherChannel().unbind()
      }
      const channel = this.$pusher.subscribe(cartId)
      commit('setPusherChannel', channel)
      channel.bind('cart-change', (data) => {
        if (data.tabId !== this.$tabId) {
          dispatch('refresh')
        }
      })
      commit('setCart', cart)
      commit('setGuestEmail', storage.getItem('yellaw-guest-email') || null)
      commit('setReady', true)
    },
    async persist ({ state, commit }) {
      const cartPayload = JSON.parse(JSON.stringify(omit(state.cart, ['total', '_products'])))
      const effectiveEmail = state.guestEmail || this.state.auth.email
      if (effectiveEmail && cartPayload.shipping?.properties?.deliveryAddress) {
        cartPayload.shipping.properties.deliveryAddress.email = effectiveEmail
      }
      await this.$api.put(`/carts/${state.cart.id}`, cartPayload)
      // We must update products in cart to update pricing
      const { data: cart } = await this.$api.get(`/carts/${state.cart.id}`)
      commit('setCart', {
        ...state.cart,
        _products: cart._products
      })
    },
    async refresh ({ state, commit }) {
      const { data: cart } = await this.$api.get(`/carts/${state.cart.id}`)
      commit('setCart', cart)
    },
    async addProduct ({ commit, state, dispatch }, { sku, label, cover, quantity, pricing, properties }) {
      // Filet de rattrapage : si le panier n'a pas pu se charger au démarrage, on
      // retente ici plutôt que de laisser le client avec un bouton sans effet.
      if (!state.cart) {
        if (this.$trace) {
          this.$trace('panier_absent_reinit', { sku })
        }
        await dispatch('init')
      }
      const cart = JSON.parse(JSON.stringify(state.cart))
      let initialQuantity = 0
      const existingItem = cart.products.find(p => p.sku === sku)
      if (existingItem) {
        initialQuantity = existingItem.quantity
      }
      let newQuantity = initialQuantity + quantity
      if (newQuantity < 0) {
        newQuantity = 0
      }
      const getPrice = () => {
        const sortedDecreasingPrices = orderBy(pricing.decreasing_prices, ['minimal_quantity'], ['desc'])
        for (const dp of sortedDecreasingPrices) {
          if (dp.minimal_quantity < newQuantity) {
            return this.getters['auth/paysTaxes'] ? dp.unit_taxed_price : dp.unit_price
          }
        }
        return this.getters['auth/paysTaxes'] ? pricing.unit_taxed_price : pricing.unit_price
      }
      const price = getPrice()
      const newObject = omitBy({
        sku,
        label,
        cover,
        quantity: newQuantity,
        properties,
        price
      }, isUndefined)
      if (newQuantity === 0 && existingItem) {
        cart.products.splice(cart.products.indexOf(existingItem), 1)
      } else if (existingItem) {
        Object.assign(existingItem, newObject)
      } else {
        cart.products.unshift(Object.assign({
          properties: properties || {}
        }, newObject))
      }
      commit('setCart', cart)
      await dispatch('persist')
      return {
        price
      }
    },
    async removeProduct ({ commit, state, dispatch }, { sku }) {
      const cart = JSON.parse(JSON.stringify(state.cart))
      const items = cart.products.filter(p => p.sku === sku)
      for (const item of items) {
        cart.products.splice(cart.products.indexOf(item), 1)
      }
      commit('setCart', cart)
      await dispatch('persist')
    },
    async flushCart ({ commit, state, dispatch }) {
      const cart = JSON.parse(JSON.stringify(state.cart))
      cart.products = []
      commit('setCart', cart)
      await dispatch('persist')
    },
    async updateProductPrice ({ commit, state, dispatch }, { sku, price }) {
      const cart = JSON.parse(JSON.stringify(state.cart))
      const productItem = cart.products.find(p => p.sku === sku)
      if (productItem) {
        productItem.price = price
        commit('setCart', cart)
        await dispatch('persist')
      }
    },
    async updateManyProductPrices ({ commit, state, dispatch }, inputs) {
      const cart = JSON.parse(JSON.stringify(state.cart))
      let updated = false
      for (const { sku, price, crossedPrice } of inputs) {
        const productItem = cart.products.find(p => p.sku === sku)
        if (productItem) {
          updated = true
          productItem.price = price
          if (crossedPrice) {
            productItem.properties.crossed_price = crossedPrice
          }
        }
      }

      if (updated) {
        commit('setCart', cart)
        await dispatch('persist')
      }
    },
    async updateProductQuantity ({ commit, state, dispatch }, { sku, quantity }) {
      const cart = JSON.parse(JSON.stringify(state.cart))
      const productItem = cart.products.find(p => p.sku === sku)
      if (productItem) {
        productItem.quantity = quantity
        commit('setCart', cart)
        await dispatch('persist')
      }
    },
    async setGuestEmail ({ commit, state, dispatch }, email) {
      storage.setItem('yellaw-guest-email', email)
      commit('setGuestEmail', email)
      if (state.cart.firebase_user_id) {
        const cart = JSON.parse(JSON.stringify(state.cart))
        cart.firebase_user_id = null
        commit('setCart', cart)
        await dispatch('persist')
      }
    },
    async setFirebaseUserId ({ commit, state, dispatch }, userId) {
      const cart = JSON.parse(JSON.stringify(state.cart))
      cart.firebase_user_id = userId
      commit('setCart', cart)
      await dispatch('persist')
    },
    // eslint-disable-next-line camelcase
    async setShipping ({ commit, state, dispatch }, { shipping_service_id, shipping_service_name, price, cover, properties }) {
      const cart = JSON.parse(JSON.stringify(state.cart))
      Object.assign(cart.shipping, omitBy({
        shipping_service_id,
        shipping_service_name,
        price,
        cover
      }, isUndefined))
      if (properties) {
        Object.assign(cart.shipping.properties, properties)
      }
      const effectiveEmail = state.guestEmail || this.state.auth.email
      if (effectiveEmail && cart.shipping.properties?.deliveryAddress) {
        cart.shipping.properties.deliveryAddress.email = effectiveEmail
      }
      commit('setCart', cart)
      await dispatch('persist')
    },
    async fetchWishlists ({ commit }) {
      const { data } = await this.$api.get('/wishlists')
      commit('setWishlists', data)
    }
  },
  getters: {
    itemsCount (state) {
      if (!state.cart) {
        return null
      }
      let total = 0
      for (const item of state.cart.products) {
        total += item.quantity
      }
      return total
    },
    total (state, getters) {
      return getters.productsTotal + getters.shippingTotal - state.cart.coupons_total
    },
    productsTotal (state) {
      if (!state.cart) {
        return 0
      }
      let total = 0
      for (const item of state.cart.products) {
        total += item.quantity * item.price
      }
      return total
    },
    couponsTotal (state) {
      if (!state.cart) {
        return 0
      }
      return state.cart.coupons_total || 0
    },
    shippingTotal (state) {
      if (!state.cart) {
        return 0
      }
      return state.cart.shipping.price
    }
  }
}
